import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import SInput from '../../../components/stringInput/StringInput.component'
import Validation from '../../../components/validation/Validation.component'
import {
  IonCol,
  IonGrid,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { AuthorizationInformationConstants } from '../store/authorizationInformation.constants'
import {
  setTransportAuthorizationCode,
  setJustificationForTransfer,
  saveAuthorizationInfo,
  setReasonForInterfacilityTransfer,
  setReasonForInterfacilityTransferPNNV,
} from '../store/authorizationInformation.actions'
import { getAuthorizationInformationSelector } from '../selectors'
import { getAuthorizationInformationOptionsSelector } from '../../options/selectors'
import { IOption, IPNNVOption } from '../../../common/common.types'

const AuthorizationInformationContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isTablet } = useDeviceMetrics()
  const authorizationInformation = useSelector(getAuthorizationInformationSelector)
  const {
    justificationForTransfer,
    transportAuthorizationCode,
    reasonForInterfacilityTransfer,
    reasonForInterfacilityTransferPNNV,
  } = authorizationInformation

  const authorizationInformationOptions = useSelector(getAuthorizationInformationOptionsSelector)
  const { interfacilityTransferOptions } = authorizationInformationOptions

  const [flag, setFlag] = useState(true)

  const setTransportAuthorizationCodeOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setTransportAuthorizationCode(e.detail.value))
    save()
  }

  const setReasonForInterfacilityTransferOnChange = (option: IOption) => {
    dispatch(setReasonForInterfacilityTransfer(option))
    save()
  }

  const setReasonForInterfacilityTransferPNNVOnChange = (option: IPNNVOption) => {
    dispatch(setReasonForInterfacilityTransferPNNV(option))
    save()
  }

  const setJustificationForTransferOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setJustificationForTransfer(e.detail.value))
    save()
  }

  const save = () => {
    dispatch(saveAuthorizationInfo())
  }

  useEffect(() => {
    if (flag) {
      transportAuthorizationCode && dispatch(setTransportAuthorizationCode(transportAuthorizationCode))
      justificationForTransfer && dispatch(setJustificationForTransfer(justificationForTransfer))
    }
  }, [flag])

  useIonViewDidEnter(() => {
    setFlag(true)
  })

  useIonViewDidLeave(() => {
    setFlag(false)
  })

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('authorizationInformation.details.AuthorizationDetails')}
        </IonLabel>
      </IonItemDivider>

      <IonList className={isTablet ? 'ion-margin-horizontal' : ''}>
        <IonGrid className='no-padding'>
          <IonRow>
            <IonCol className='picklist'>
              <SInput
                label={t('authorizationInformation.details.TransportAuthorizationCode')}
                dataTestID='TransportAuthorizationCode'
                inputValue={transportAuthorizationCode}
                onChangeAction={setTransportAuthorizationCodeOnChange}
              />
              <Validation id={AuthorizationInformationConstants.SET_TRANSPORTAUTHORIZATIONCODE} />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='ReasonForInterfacilityTransfer'
                selectedOption={reasonForInterfacilityTransfer}
                listHeader={t('authorizationInformation.details.ReasonForInterfacilityTransfer')}
                label={t('authorizationInformation.details.ReasonForInterfacilityTransfer')}
                options={interfacilityTransferOptions}
                action={setReasonForInterfacilityTransferOnChange}
                selectedPNNVOption={reasonForInterfacilityTransferPNNV}
                pnnvAction={setReasonForInterfacilityTransferPNNVOnChange}
              />
              <Validation id={AuthorizationInformationConstants.SET_REASONFORINTERFACILITYTRANSFER} />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <SInput
                label={t('authorizationInformation.details.JustificationForTransfer')}
                dataTestID='JustificationForTransfer'
                inputValue={justificationForTransfer}
                onChangeAction={setJustificationForTransferOnChange}
              />
              <Validation id={AuthorizationInformationConstants.SET_JUSTIFICATIONFORTRANSFER} />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
        </IonGrid>
      </IonList>
      <IonItemDivider color='light' />
    </>
  )
}

export default AuthorizationInformationContent
